import MuiLink from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { CANDIDATE_STATUS } from 'modules/hiring/shared/constants';
import { getCandidateStatusMeta } from 'modules/hiring/shared/utils';
import { Table } from 'modules/shared/components';

export default function CandidatesTable({ candidatesData = {}, status, loading }) {
  const [searchParams] = useSearchParams();
  const activeData = useMemo(() => candidatesData[status] ?? [], [candidatesData, status]);

  const columns = [
    {
      id: 'applicationDate',
      dataKey: 'applicationDate',
      label: 'Application Date',
      sortable: true,
    },
    { id: 'name', dataKey: 'name', label: 'Name', sortable: true },
    { id: 'contact', dataKey: 'contact', label: 'Contact', sortable: true },
    { id: 'email', dataKey: 'email', label: 'Email', sortable: true },
    {
      id: 'designation',
      dataKey: 'designation',
      label: 'Department/Designation',
      sortable: true,
      renderCell: (_, row) => (
        <div>
          <span>{row.department}</span>
          <br />
          <span>{row.designation}</span>
        </div>
      ),
    },
    {
      id: 'status',
      dataKey: 'status',
      label: 'Status',
      sortable: true,
      renderCell: (_, row) => {
        const { status, label, color, roundLabel } = getCandidateStatusMeta(row);

        return (
          <>
            <Typography sx={{ color, fontWeight: 500, fontSize: '0.75rem' }}>{label}</Typography>

            {status === CANDIDATE_STATUS.SCHEDULED && (
              <Typography sx={{ fontSize: '0.75rem' }}>{row.nextInterview}</Typography>
            )}

            {status === CANDIDATE_STATUS.REJECTED && row.currentInterviewRound !== null && (
              <Typography sx={{ fontSize: '0.75rem' }}>{roundLabel}</Typography>
            )}

            {status === CANDIDATE_STATUS.REJECTED && (
              <Typography sx={{ fontSize: '0.75rem' }}>{row.rejectedDate}</Typography>
            )}
          </>
        );
      },
    },
    {
      id: 'action',
      dataKey: 'id',
      label: 'More details',
      renderCell: (_, row) => (
        <MuiLink
          component={Link}
          to={{ pathname: `/candidates/${row.id}`, search: searchParams.toString() }}
          sx={{
            color: 'grey.500',
            '&:visited': { color: 'grey.500' },
            textDecoration: 'underline',
          }}
        >
          view
        </MuiLink>
      ),
    },
  ];

  const emptyMessageMap = {
    [CANDIDATE_STATUS.APPLIED]: 'No candidates have applied yet.',
    [CANDIDATE_STATUS.SCHEDULED]: 'No interviews are currently scheduled.',
    [CANDIDATE_STATUS.REJECTED]: 'No rejected candidates to show.',
  };

  return (
    <Table
      columns={columns}
      data={activeData}
      loading={loading}
      emptyMessage={emptyMessageMap[status] ?? 'No candidates found.'}
      resetSortKey={status}
    />
  );
}
